<script lang="ts">
  export const LICENSE_WARNINGS = {
    EXPIRES_SOON: 'expires_soon',
    EXPIRED: 'expired',
    MISSING_EXPIRATION: 'missing_expiration_date',
    PRODUCTS_ARCHIVED_SOON: 'license_products_archived_soon',
    PRODUCTS_ARCHIVED: 'license_products_archived',
  };
</script>

<script setup lang="ts">
  import useModals from '@leaflink/stash/useModals.vue';
  import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
  import startOfTomorrow from 'date-fns/startOfTomorrow';
  import { storeToRefs } from 'pinia';
  import { defineAsyncComponent, h, ref, watch } from 'vue';
  import { I18nT } from 'vue-i18n';
  import { RouterLink, useRouter } from 'vue-router';

  import LLBanner from '@/components/LLBanner/LLBanner.vue';
  import { CUSTOMER_SUPPORT_URL } from '@/constants';
  import { ROUTE_NAMES } from '@/constants/routeNames';
  import { GlobalBanner } from '@/models/company';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import getCookie from '@/utils/getCookie';

  const LicenseProductsArchivedWarningModal = defineAsyncComponent(
    () => import('@/components/modals/LicenseProductsArchivedWarningModal.vue'),
  );
  const LicenseProductsArchivedModal = defineAsyncComponent(
    () => import('@/components/modals/LicenseProductsArchivedModal.vue'),
  );

  const router = useRouter();

  const companyStore = useCompanyStore();
  const { activeCompany } = storeToRefs(companyStore);

  const userStore = useUserStore();
  const { userActiveCompanyPermissions: permissions, getIsSplitEnabled } = storeToRefs(userStore);

  const globalBanners = ref<GlobalBanner[]>([]);

  /**
   * For notices related to company licenses.
   * At the moment, only one license banner gets displayed based on warning priority.
   */
  function getLicenseBanner() {
    if (!permissions.value?.staffIsAdmin) {
      return;
    }

    const licenses = activeCompany.value?.licenses || [];
    const archivedLicenses = activeCompany.value?.archivedLicenses || [];

    const hasArchivedLicenses = !!activeCompany.value?.archivedLicenses?.length;
    const isBrandOwner = !!activeCompany.value?.isBrandOwner;

    const hasActiveLicenses = licenses.length;
    const missingExpirationDates = licenses.filter((l) => l.warning === LICENSE_WARNINGS.MISSING_EXPIRATION);
    const expired = licenses.filter((l) => l.warning === LICENSE_WARNINGS.EXPIRED);
    const expiresSoon = licenses.filter((l) => l.warning === LICENSE_WARNINGS.EXPIRES_SOON);

    // example warning value: license_products_archived_soon:2022-08-26T00:00:00.000Z
    const productsArchivedWarningLicense = archivedLicenses.find(
      (l) => l.warning?.split(':')[0] === LICENSE_WARNINGS.PRODUCTS_ARCHIVED_SOON,
    );
    // example warning value: license_products_archived:2022-08-26T00:00:00.000Z
    const productsArchivedLicense = archivedLicenses.find(
      (l) => l.warning?.split(':')[0] === LICENSE_WARNINGS.PRODUCTS_ARCHIVED,
    );

    const licensesRouteConfig = {
      name: ROUTE_NAMES.LICENSES,
      params: {
        company: activeCompany.value?.slug,
      },
    };

    const banner: GlobalBanner = {
      link: getIsSplitEnabled.value('ssr_iframe') ? undefined : router.resolve(licensesRouteConfig).href,
      to: getIsSplitEnabled.value('ssr_iframe') ? licensesRouteConfig : undefined,
      callToAction: i18n.t('common.clickToUpdate'),
      canClose: true,
      openInNewTab: false,
      priority: 'medium',
      name: 'license',
      color: 'orange',
      message: '',
      reshow: 1,
    };

    /**
     * Banner: Products Archived (products associated with an archived license)
     */
    if (productsArchivedLicense) {
      banner.message = i18n.t('notify.licenseWarnings.productsArchived', {
        license: productsArchivedLicense.number,
      });
      banner.callToAction = i18n.t('common.contactSupport');
      banner.link = CUSTOMER_SUPPORT_URL;
      banner.to = undefined;
      banner.canClose = false;
      banner.color = 'red';
      banner.name = 'products-archived';

      const dismissCookieName = `mp-${banner.name}-${activeCompany.value?.id}`;

      const modals = useModals();
      if (!getCookie(dismissCookieName)) {
        modals.open({
          component: LicenseProductsArchivedModal,
          name: banner.name,
          attributes: {
            license: productsArchivedLicense,
            onDismiss: () => {
              // dismiss modal for remainder of the day only
              const expiryDate = startOfTomorrow();
              // TODO: Update setCookie to accept a Date object
              // https://leaflink.atlassian.net/browse/TRACE-2320
              document.cookie = `${dismissCookieName}=true;path=/;expires=${expiryDate.toUTCString()}`;
              modals.close();
            },
          },
        });
      }

      return banner;
    }

    /**
     * Banner: Products to be Archived
     */
    if (productsArchivedWarningLicense) {
      // example warning value: license_archived:2022-08-26T00:00:00.000Z
      // the date reflects when the products associated with the archived license will get archived
      const productArchiveDateString = productsArchivedWarningLicense.warning?.substring(
        productsArchivedWarningLicense.warning?.indexOf(':') + 1,
      );
      const currentDate = new Date();
      const productArchiveDate = new Date(productArchiveDateString || currentDate);
      const numberOfDays = differenceInCalendarDays(productArchiveDate, currentDate);
      const daysLeft = numberOfDays > 0 ? numberOfDays : 0;

      banner.message = i18n.tc('notify.licenseWarnings.productsArchivedWarning', 5, {
        license: productsArchivedWarningLicense.number,
        daysLeft: String(daysLeft),
      });
      banner.callToAction = i18n.t('common.contactSupport');
      banner.link = CUSTOMER_SUPPORT_URL;
      banner.to = undefined;
      banner.canClose = false;
      banner.name = 'products-archived-warning';

      const dismissCookieName = `mp-${banner.name}-${activeCompany.value?.id}`;

      const modals = useModals();
      if (!getCookie(dismissCookieName)) {
        modals.open({
          component: LicenseProductsArchivedWarningModal,
          name: banner.name,
          attributes: {
            license: productsArchivedWarningLicense,
            daysLeft: String(daysLeft),
            onDismiss: () => {
              // dismiss modal for remainder of the day only
              const expiryDate = startOfTomorrow();
              // TODO: Update setCookie to accept a Date object
              // https://leaflink.atlassian.net/browse/TRACE-2320
              document.cookie = `${dismissCookieName}=true;path=/;expires=${expiryDate.toUTCString()}`;
              modals.close();
            },
          },
        });
      }

      return banner;
    }

    /**
     * Banner: No active license
     */
    if (!isBrandOwner && !hasActiveLicenses && hasArchivedLicenses) {
      banner.message = () =>
        h(
          I18nT,
          { keypath: 'notify.licenseWarnings.noActiveLicense' },
          {
            ctaLink: () =>
              h(
                RouterLink,
                {
                  class: 'tw-text-white tw-underline',
                  to: { name: ROUTE_NAMES.LICENSES, params: { company: activeCompany.value?.slug } },
                },
                i18n.t('notify.licenseWarnings.noActiveLicenseLink'),
              ),
          },
        );
      banner.callToAction = '';
      banner.canClose = false;
      // the custom name prevents the banner from not rendering if the expiry banners were previously dismissed.
      banner.name = 'no-active-license';

      return banner;
    }

    /**
     * Banner: Get started (no licenses yet)
     */
    if (!isBrandOwner && !hasActiveLicenses && !hasArchivedLicenses) {
      banner.message = () =>
        h(
          I18nT,
          { keypath: 'notify.licenseWarnings.getStarted' },
          {
            ctaLink: () =>
              h(
                RouterLink,
                {
                  class: 'tw-text-white tw-underline',
                  to: { name: ROUTE_NAMES.LICENSES, params: { company: activeCompany.value?.slug } },
                },
                i18n.t('notify.licenseWarnings.getStartedLink'),
              ),
          },
        );
      banner.callToAction = '';
      banner.canClose = false;
      // the custom name scopes the banner from not rendering if the expiry banners were previously dismissed.
      banner.name = 'get-started';

      return banner;
    }

    /**
     * Banner: Multiple licenses expire soon
     */
    if (expiresSoon.length > 1) {
      banner.message = i18n.t('notify.licenseWarnings.multipleExpiresSoon');
      return banner;
    }

    /**
     * Banner: Single license expires soon
     */
    if (expiresSoon.length === 1) {
      banner.message = i18n.t('notify.licenseWarnings.singleExpiresSoon', { number: expiresSoon[0].number });
      return banner;
    }

    /**
     * Banner: Multiple licenses expired
     */
    if (expired.length > 1) {
      banner.message = i18n.t('notify.licenseWarnings.multipleExpired');
      return banner;
    }

    /**
     * Banner: Single license expired
     */
    if (expired.length === 1) {
      banner.message = i18n.t('notify.licenseWarnings.singleExpired', { number: expired[0].number });
      return banner;
    }

    /**
     * Banner: License missing expiration
     */
    if (missingExpirationDates.length) {
      banner.message = i18n.t('notify.licenseWarnings.missingExpiration');
      return banner;
    }

    return null;
  }

  /**
   * Site banner is managed by the CMS and can be used for general announcements.
   * Has lower priority than license banners.
   */
  function getCmsBanner() {
    const siteBanner = activeCompany.value?.siteBanner;

    return siteBanner
      ? ({
          link: siteBanner.link,
          callToAction: siteBanner.callToAction,
          openInNewTab: !!siteBanner.openInNewTab,
          priority: 'low',
          name: 'cms-banner',
          color: 'teal',
          message: siteBanner.content,
          reshow: 30,
        } as GlobalBanner)
      : null;
  }

  function getBanners() {
    const banners: GlobalBanner[] = [];
    const licenseBanner = getLicenseBanner();
    const cmsBanner = getCmsBanner();

    if (licenseBanner) {
      banners.push(licenseBanner);
    }

    if (cmsBanner) {
      banners.push(cmsBanner);
    }

    globalBanners.value = banners;
  }

  watch(
    () => activeCompany.value,
    () => {
      getBanners();
    },
    { immediate: true },
  );
</script>

<template>
  <LLBanner v-if="globalBanners.length" :banners="globalBanners" />
</template>
